import { useState } from "react";
import type { Album } from "@shared/types";

export function AlbumConfirm({
  album,
  onSave,
  onCancel,
}: {
  album: Album;
  onSave: (a: Album) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(album.title);
  const [artist, setArtist] = useState(album.artist);
  const [year, setYear] = useState(album.year?.toString() ?? "");
  const [matrix, setMatrix] = useState(album.matrix_number);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSave({
        ...album,
        uuid: album.uuid || crypto.randomUUID(),
        title: title.trim(),
        artist: artist.trim(),
        matrix_number: matrix.trim(),
        year: year.trim() ? Number(year) : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-400">
        {album.label && <div>{album.label}</div>}
        {album.format && <div>{album.format}</div>}
        {album.barcode && <div className="font-mono text-xs">UPC {album.barcode}</div>}
      </div>
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Artist">
        <input value={artist} onChange={(e) => setArtist(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Year">
        <input
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Matrix number (engraving on inner ring)">
        <input value={matrix} onChange={(e) => setMatrix(e.target.value)} className={inputCls} />
      </Field>
      <div className="flex gap-2 pt-2">
        <button
          onClick={submit}
          disabled={submitting || !title.trim() || !artist.trim()}
          className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:text-gray-300 rounded font-medium"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded">
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-gray-300">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";
